import { LearningObjectTypes } from '@hendrx/lms/constants';
import { useViewportSize } from '@mantine/hooks';

import { useReadOnly } from './ReadOnlyProvider';
import { MOBILE_BREAKPOINT, TABLET_BREAKPOINT } from '../client/consts/breakpoints';
import { useLearningObjectCommonData } from '../components/providers/LearningObjectCommonDataProvider';
import { useUser } from '../queries/user';
import { DeviceType } from '../types/DeviceType';

export const BASE_FONT_SIZE = 16;
const PAGE_MAX_WIDTH = 1920;

const calculateFontSize = (width: number, minFontSize: number) => {
  //Font size is calculated by Fluid Typography formula:
  //font-size: calc([minimum size] + ([maximum size] - [minimum size]) * ((100vw - [minimum viewport width]) / ([maximum viewport width] - [minimum viewport width])));
  return (
    minFontSize + (BASE_FONT_SIZE - minFontSize) * ((width - TABLET_BREAKPOINT) / (PAGE_MAX_WIDTH - TABLET_BREAKPOINT))
  );
};

export type DeviceViewData = {
  deviceView: DeviceType;
  htmlFontSize: number;
  htmlFontSizeByWidth: number;
  isMobileOrTabletView: boolean;
  isMobileOrTabletWidth: boolean;
  isMobileView: boolean;
  isTabletView: boolean;
  isPCView: boolean;
};

type UseDeviceViewArgs = {
  minFontSize?: number;
  baseOnWidgetWidth?: boolean;
};

export const useDeviceView = (args?: UseDeviceViewArgs): DeviceViewData => {
  const { minFontSize = 10 } = args ?? {};
  const { user } = useUser();

  const { width } = useViewportSize();
  const { learningObjectType } = useLearningObjectCommonData();
  const { isReadOnly } = useReadOnly();

  const keepPCView =
    learningObjectType === LearningObjectTypes.PlenaryLesson &&
    (user?.role === 'teacher' || user?.role === 'admin' || (isReadOnly && user?.role === 'student'));

  const pcFontSize = calculateFontSize(width, minFontSize);
  const tabletFontSize = (BASE_FONT_SIZE * width) / TABLET_BREAKPOINT;
  const mobileFontSize = (BASE_FONT_SIZE * width) / MOBILE_BREAKPOINT;

  if (keepPCView || width > TABLET_BREAKPOINT) {
    const htmlFontSize = keepPCView
      ? width > TABLET_BREAKPOINT
        ? pcFontSize
        : width > MOBILE_BREAKPOINT
        ? tabletFontSize
        : mobileFontSize
      : pcFontSize;
    return {
      deviceView: 'pc',
      htmlFontSize,
      htmlFontSizeByWidth: pcFontSize,
      isMobileOrTabletView: false,
      isMobileOrTabletWidth: width <= TABLET_BREAKPOINT,
      isMobileView: false,
      isTabletView: false,
      isPCView: true
    };
  } else if (width > MOBILE_BREAKPOINT) {
    return {
      deviceView: 'tablet',
      htmlFontSize: tabletFontSize,
      htmlFontSizeByWidth: pcFontSize,
      isMobileOrTabletView: true,
      isMobileOrTabletWidth: true,
      isMobileView: false,
      isTabletView: true,
      isPCView: false
    };
  } else {
    return {
      deviceView: 'mobile',
      htmlFontSize: mobileFontSize,
      htmlFontSizeByWidth: pcFontSize,
      isMobileOrTabletView: true,
      isMobileOrTabletWidth: true,
      isMobileView: true,
      isTabletView: false,
      isPCView: false
    };
  }
};
